import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import { Link } from "react-router-dom";

// SVG Icon for "Read More" button
const ReadMoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    fill="currentColor"
    className="bi bi-arrow-right-circle-fill"
    viewBox="0 0 16 16"
    style={{ marginLeft: "8px" }}
  >
    <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM4.5 7.5a.5.5 0 0 0 0 1h5.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H4.5z" />
  </svg>
);

// Placeholder image function is no longer needed as images are removed
// const getPlaceholderImage = (title) => { ... }

const BlogPage = () => {
  const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/"; // Fallback for local dev
  const {
    data: blogPosts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["all-blogs-dynamic"], // Changed queryKey to avoid conflict
    queryFn: async () => {
      const res = await axios.get(`${baseUrl}blog`);
      // Assuming the API returns an array of posts directly or under a 'data' field
      return Array.isArray(res.data.data)
        ? res.data.data
        : Array.isArray(res.data)
        ? res.data
        : [];
    },
  });

  const [filter, setFilter] = useState("latest");

  // Sorting logic
  const sortedPosts = React.useMemo(() => {
    if (!blogPosts) return [];
    const postsCopy = [...blogPosts]; // Create a copy before sorting
    return postsCopy.sort((a, b) => {
      if (filter === "latest") {
        return (
          new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
        ); // Prefer createdAt if available
      } else if (filter === "popular") {
        return (b.views || 0) - (a.views || 0);
      }
      return 0;
    });
  }, [blogPosts, filter]);

  return (
    <section className="blog-page-section">
      {" "}
      {/* Removed bg-light, hero will have its own */}
      <style>
        {`
          :root {
            --bs-primary-rgb: 15, 67, 114; /* For Bootstrap components */
            --bs-primary: #0f4372;
          }
          .blog-page-section {
            background-color: #f4f7f9; 
          }
          .blog-hero {
            background: linear-gradient(rgb(217 58 56), rgb(217 58 56 / 86%)), url(https://images.unsplash.com/photo-1470790376778-a9fbc86d70e2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1900&q=80) no-repeat center center;
            background-size: cover;
            border-radius: 0 0 40px 40px; 
            padding: 6rem 0; 
          }
          .blog-hero .display-4 {
            font-size: 3.5rem; 
            text-shadow: 2px 2px 5px rgba(0,0,0,0.4);
          }
          .blog-hero .lead {
            font-size: 1.25rem;
            color: rgba(255,255,255,0.85) !important; /* Ensure lead text is light */
          }

          .filter-select { /* Styling for the filter dropdown */
            border-color: #0f4372;
            color: #0f4372;
            font-weight: 500;
            box-shadow: 0 2px 5px rgba(15, 67, 114, 0.1);
          }
          .filter-select:focus {
            border-color: #0f4372;
            box-shadow: 0 0 0 0.25rem rgba(15, 67, 114, 0.25);
          }

          .dynamic-blog-card {
            background: #ffffff; 
            border-radius: 16px; 
            box-shadow: 0 8px 25px rgba(0,0,0,0.08); 
            padding: 25px;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            height: 100%; 
            display: flex;
            flex-direction: column;
            border: none; /* Removed default Bootstrap border */
          }
          .dynamic-blog-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 12px 30px rgba(0,0,0,0.12);
          }
          /* .dynamic-blog-card-image CSS class removed as images are no longer displayed */
          
          .dynamic-blog-category-badge {
            display: inline-block;
            background-image: linear-gradient(to right, #e5272e, #f59332); /* Gradient for badge */
            color: #ffffff; 
            padding: 0.3em 0.8em; /* Slightly more padding */
            font-size: 0.75rem;
            border-radius: 20px; 
            font-weight: 600;
            margin-bottom: 1rem; /* Increased margin-bottom as image is removed */
            text-transform: uppercase;
            letter-spacing: 0.5px;
            align-self: flex-start; /* Align badge to the start */
          }
          .dynamic-blog-card-title {
            color: #0f4372; 
            font-weight: 700; 
            font-size: 1.3rem; 
            margin-bottom: 0.5rem;
            line-height: 1.3;
          }
          .dynamic-blog-card-meta {
            font-size: 0.8rem;
            color: #6c757d; 
            margin-bottom: 1rem;
          }
          .dynamic-blog-card-excerpt {
            font-size: 0.9rem;
            color: #495057; 
            line-height: 1.6;
            flex-grow: 1; 
            margin-bottom: 1.5rem; 
          }
          .btn-read-more-dynamic { 
            background-image: linear-gradient(to right, #e5272e, #f59332);
            color: white;
            border: none;
            padding: 10px 22px; 
            border-radius: 50px; /* Pill shape button */
            font-weight: 600;
            text-decoration: none;
            display: inline-flex; 
            align-items: center;
            transition: opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
            align-self: flex-start; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .btn-read-more-dynamic:hover {
            opacity: 0.9;
            color: white; 
            transform: translateY(-2px) scale(1.02);
            box-shadow: 0 4px 12px rgba(229, 39, 46, 0.3); /* Shadow with gradient color */
          }
          .loading-state, .error-state {
            text-align: center;
            padding: 3rem;
            color: #555;
          }
        `}
      </style>
      <div className="blog-hero text-center mb-5">
        <div className="container">
          <h1 className="display-4 fw-bold text-white mb-3">Booze247 Blog</h1>
          <p className="lead text-white-50">
            News, tips, and behind-the-scenes from Cardiff’s favorite 24/7
            delivery service.
          </p>
        </div>
      </div>
      <div className="container pb-5">
        <div className="d-flex justify-content-between align-items-center mb-4 pt-3">
          <h2 className="fw-semibold" style={{ color: "#0f4372" }}>
            Latest Articles
          </h2>
          <select
            className="form-select w-auto filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="latest">Sort by: Latest</option>
            <option value="popular">Sort by: Popular</option>
          </select>
        </div>

        {isLoading && <div className="loading-state">Loading posts...</div>}
        {error && (
          <div className="error-state">
            Error fetching posts: {error.message}
          </div>
        )}

        {!isLoading && !error && sortedPosts.length === 0 && (
          <div className="text-center py-5">
            <p className="lead text-muted">
              No blog posts found at the moment. Check back soon!
            </p>
          </div>
        )}

        {!isLoading && !error && sortedPosts.length > 0 && (
          <div className="row g-4">
            {sortedPosts.map((post) => (
              <div
                className="col-md-6 col-lg-4 d-flex"
                key={post._id || post.id}
              >
                {" "}
                {/* Use post._id if available */}
                <div className="dynamic-blog-card w-100">
                  {/* Image display removed */}
                  {/* <img 
                    src={post.image || getPlaceholderImage(post.title)} 
                    alt={post.title} 
                    className="dynamic-blog-card-image" 
                    onError={(e) => { e.target.onerror = null; e.target.src=getPlaceholderImage(post.title); }} // Fallback for broken image links
                  /> 
                  */}

                  {/* Display category if available */}
                  {post.category && (
                    <span className="dynamic-blog-category-badge">
                      {post.category}
                    </span>
                  )}

                  <h5 className="dynamic-blog-card-title">{post.title}</h5>
                  <h6 className="dynamic-blog-card-meta small">
                    By {post.author || "Team Booze247"} |{" "}
                    {moment(post.createdAt || post.date).format("MMMM D, YYYY")}{" "}
                    {/* Corrected date format */}
                  </h6>
                  {/* Use post.excerpt or a snippet of post.content */}
                  <p className="dynamic-blog-card-excerpt">
                    {post.excerpt.slice(0, 120) ||
                      (post.content.slice(0, 120)
                        ? post.content.substring(0, 120) + "..."
                        : "No content preview available.")}
                  </p>
                  <div className="mt-auto">
                    {" "}
                    {/* Pushes button to the bottom */}
                    <Link
                      to={`/blog/${post._id || post.id}`}
                      className="btn-read-more-dynamic"
                    >
                      Read More <ReadMoreIcon />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogPage;
